import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from 'react'
import type { AssetVizLocaleConfig } from './AssetVizLocaleConfig'
import { defaultLocaleConfig } from './defaultLocaleConfig'

const AssetVizLocaleContext =
  createContext<AssetVizLocaleConfig>(defaultLocaleConfig)

/**
 * Provider that injects locale-aware formatting functions into the AssetViz
 * component tree. The host application wraps AssetViz with this provider
 * and supplies its own formatters that respect the user's locale, measurement
 * system (metric/imperial), and number formatting preferences.
 *
 * Only the formatters that differ from defaults need to be provided — the
 * config is merged with defaultLocaleConfig so consumers can override
 * selectively.
 */
export const AssetVizLocaleProvider: FC<{
  config: Partial<AssetVizLocaleConfig>
  children: ReactNode
}> = ({ config, children }) => {
  const merged = useMemo<AssetVizLocaleConfig>(
    () => ({
      ...defaultLocaleConfig,
      ...config,
    }),
    [config]
  )

  return (
    <AssetVizLocaleContext.Provider value={merged}>
      {children}
    </AssetVizLocaleContext.Provider>
  )
}

/**
 * Hook to access locale formatting functions from the nearest
 * AssetVizLocaleProvider. Falls back to basic pass-through formatters
 * if no provider is present in the tree.
 */
export function useAssetVizLocale(): AssetVizLocaleConfig {
  return useContext(AssetVizLocaleContext)
}
